import {
    DataGrid,
    GridActionsCellItem,
    GridColDef,
    GridEventListener,
    GridRenderEditCellParams,
    GridRowId,
    GridRowModes,
    GridRowModesModel,
    GridRowParams,
    GridToolbarContainer,
    GridTreeNodeWithRender,
    MuiEvent
} from "@mui/x-data-grid";
import React, {useEffect, useMemo, useState} from "react";

import {
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    ListItemText,
    MenuItem,
    Select,
    Stack,
    TableContainer,
    TextField,
    Typography
} from "@mui/material";
import {Edit, OpenInFull, Workspaces} from "@mui/icons-material";
import {useDocumentTitle} from "../../Hooks/useDocumentTitle";
import {useApplicationContext} from "../../ApplicationContext";
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import {AddHostSystemData, GetFilterTagData, GetHostSystemData} from "../../apiClient/Api";
import Paper from "@mui/material/Paper";
import {useTranslation} from "react-i18next";
import {SelectChangeEvent} from "@mui/material/Select";


interface EditToolbarProps {
    setRows: (newRows: (oldRows: HostSystemRow[]) => HostSystemRow[]) => void;
    setRowModesModel: (
        newModel: (oldModel: GridRowModesModel) => GridRowModesModel,
    ) => void;
}

type HostSystemRow = Omit<GetHostSystemData, 'filterTags'> & {
    filterTags: string[];
    isNew?: boolean;
    isDocumentation?: boolean;
};

const mapHostSystemRow = (row: GetHostSystemData): HostSystemRow => ({
    ...row,
    minVersion: row.minVersion ?? "",
    maxVersion: row.maxVersion ?? "",
    hostSystemCustomId: row.hostSystemCustomId ?? "",
    filterTags: row.filterTags?.map(tag => tag?.identifier || '').filter(Boolean) || [],
    isDocumentation: row.isDocumentation ?? false,
});

function EditToolbar(props: EditToolbarProps) {
    const {setRows, setRowModesModel} = props;

    const handleClick = () => {
        const id = "NEW";
        setRows((oldRows) => [...oldRows, {
            id,
            name: 'HostSystem',
            minVersion: "",
            maxVersion: "",
            description: "",
            hostSystemCustomId: "",
            filterTags: [],
            isDocumentation: false,
            isNew: true
        }]);
        setRowModesModel((oldModel) => ({
            ...oldModel,
            [id]: {mode: GridRowModes.Edit, fieldToFocus: 'name'},
        }));
    };

    return (
        <GridToolbarContainer>
            <Button color="primary" startIcon={<AddIcon/>} onClick={handleClick}>
                Host System hinzufügen
            </Button>
        </GridToolbarContainer>
    );
}


export const AdminHostSystems = () => {
    const { t } = useTranslation();
    useDocumentTitle("base.pages.systems")
    const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});
    const [selectedRow, setSelecedRow] = useState<GridRowId | undefined>(undefined);
    const [rows, setRows] = useState<HostSystemRow[]>([])
    const [isFetching, setIsFetching] = useState(false);
    const [isData, setIsData] = useState(false);
    const context = useApplicationContext();
    const controller = context.api.fileType;
    const [deleteEntryValue,setDeleteEntryValue] = useState("")
    const [filterName, setFilterName] = useState("");
    const [filterReference, setFilterReference] = useState("");
    const [filterMinVersion, setFilterMinVersion] = useState("");
    const [filterMaxVersion, setFilterMaxVersion] = useState("");
    const [filterTags, setFilterTags] = useState<GetFilterTagData[]>([]);
    const [filterTagsSelection, setFilterTagsSelection] = useState<string[]>([]);

    const resolveFilterTagName = React.useCallback((identifier: string) => {
        return filterTags.find(tag => tag.identifier === identifier)?.name || identifier;
    }, [filterTags]);

    const renderFilterTagsEditCell = (params: GridRenderEditCellParams<HostSystemRow, string[], any, GridTreeNodeWithRender>) => {
        const currentValue = (params.value as string[]) || [];

        const handleChange = (event: SelectChangeEvent<string[]>) => {
            event.stopPropagation();
            const value = typeof event.target.value === 'string'
                ? event.target.value.split(',')
                : (event.target.value as string[]);
            params.api.setEditCellValue({ id: params.id, field: params.field, value }, event);
        };

        return (
            <Select
                multiple
                value={currentValue}
                onChange={handleChange}
                fullWidth
                renderValue={(selected) => (selected as string[]).map(resolveFilterTagName).join(', ')}
            >
                {filterTags.map((tag) => (
                    <MenuItem key={tag.identifier} value={tag.identifier}>
                        <Checkbox checked={currentValue.indexOf(tag.identifier || '') > -1} />
                        <ListItemText primary={tag.name} />
                    </MenuItem>
                ))}
            </Select>
        );
    };

    const filteredRows = useMemo<HostSystemRow[]>(() => {
        const normalize = (value: string | number | null | undefined) =>
            value === null || value === undefined ? "" : value.toString().toLowerCase();

        const nameFilter = filterName.trim().toLowerCase();
        const referenceFilter = filterReference.trim().toLowerCase();
        const minVersionFilter = filterMinVersion.trim().toLowerCase();
        const maxVersionFilter = filterMaxVersion.trim().toLowerCase();
        const tagFilterSet = new Set(filterTagsSelection.filter(Boolean));

        return rows.filter((row) => {
            const hostSystem = row as HostSystemRow;
            const matchesName = !nameFilter || normalize(hostSystem.name).includes(nameFilter);
            const matchesReference = !referenceFilter || normalize(hostSystem.hostSystemCustomId).includes(referenceFilter);
            const matchesMinVersion = !minVersionFilter || normalize(hostSystem.minVersion).includes(minVersionFilter);
            const matchesMaxVersion = !maxVersionFilter || normalize(hostSystem.maxVersion).includes(maxVersionFilter);
            const matchesTags = tagFilterSet.size === 0 || hostSystem.filterTags.some(tag => tagFilterSet.has(tag));
            return matchesName && matchesReference && matchesMinVersion && matchesMaxVersion && matchesTags;
        });
    }, [rows, filterName, filterReference, filterMinVersion, filterMaxVersion, filterTagsSelection]);

    const handleRowEditStart = (
        params: GridRowParams,
        event: MuiEvent<React.SyntheticEvent>,
    ) => {
        event.defaultMuiPrevented = true;
    };

    const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
        var row = params.row;
        event.defaultMuiPrevented = true;
    };

    const handleEditClick = (id: GridRowId) => () => {
        setRowModesModel({...rowModesModel, [id]: {mode: GridRowModes.Edit}});
    };

    const handleSaveClick = (id: GridRowId) => () => {
        setRowModesModel({...rowModesModel, [id]: {mode: GridRowModes.View}});
    };

    const handleDeleteClick = (id: GridRowId) => () => {
        setSelecedRow(id);
    };

    const handleCancelClick = (id: GridRowId) => () => {
        setRowModesModel({
            ...rowModesModel,
            [id]: {mode: GridRowModes.View, ignoreModifications: true},
        });

        const editedRow = rows.find((row) => row.id === id);
        if (editedRow!.isNew) {
            setRows(rows.filter((row) => row.id !== id));
        }
    };

    const processRowUpdate = async (newRow: HostSystemRow, oldRow: HostSystemRow) => {
        const nextRow = newRow;
        const rowId = nextRow.id ?? oldRow.id;
        if (!rowId) {
            return oldRow;
        }
        const payload: AddHostSystemData = {
            name: nextRow.name,
            description: nextRow.description,
            minVersion: nextRow.minVersion ?? "",
            maxVersion: nextRow.maxVersion ?? null,
            hostSystemCustomId: nextRow.hostSystemCustomId,
            filterTags: nextRow.filterTags || [],
            isDocumentation: nextRow.isDocumentation ?? false,
        };

        if (nextRow.isNew) {
            try {
                const response = await controller.fileTypeAddHostSystem(payload);
                if (response.data != undefined) {
                    const mapped = mapHostSystemRow(response.data);
                    setRows([...rows.filter((row) => row.id !== nextRow.id), mapped]);
                    return mapped;
                }
                return nextRow;
            } catch (e) {
                context.apiError(e)
                return oldRow;
            }
        } else {
            try {
                const response = await controller.fileTypeUpdateHostSystem(rowId, payload);
                if (response.data != undefined) {
                    const mapped = mapHostSystemRow(response.data);
                    setRows([...rows.filter((row) => row.id !== nextRow.id), mapped]);
                    return mapped;
                }
                return nextRow;
            } catch (e) {
                context.apiError(e)
                return oldRow;
            }
        }
    };

    const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
        setRowModesModel(newRowModesModel);
    };


    async function fetchData() {
        setIsFetching(true)
        try {
            const [hostSystemsResponse, filterTagsResponse] = await Promise.all([
                controller.fileTypeGetHostSystems(),
                context.api.filterTags.filterTagsListFilterTags(),
            ]);
            setRows((hostSystemsResponse.data ?? []).map(mapHostSystemRow));
            setFilterTags(filterTagsResponse.data ?? []);
        } finally {
            setIsFetching(false)
        }
    }

    useEffect(() => {
        if (!isData) {
            setIsData(true);
            fetchData();
        }

    }, [rows, isData, isFetching])

    const handleDeleteDialogClose = (save: boolean) => () => {


        if (save && selectedRow != undefined) {
            var row = selectedRow.toString();
            var value =  rows.find(r => r.id == selectedRow)?.name
            if(value == undefined)
            {
                setSelecedRow(undefined)
            }
            else{

                if(deleteEntryValue != value)
                    return;

                controller.fileTypeDeleteHostSystem(row).then(() => {
                    setRows(srows => srows.filter((item) => item.id !== row));
                    setSelecedRow(undefined);
                    fetchData();
                    setDeleteEntryValue("")
                }).catch(context.apiError)

            }
        } else {
            setSelecedRow(undefined)
            setDeleteEntryValue("")
        }
    }

    const columns: GridColDef<HostSystemRow>[] = [
        {field: "name", headerName: "Name", editable: true, type: "string", flex: 1, maxWidth: 200},
        {field: "minVersion", headerName: "Min. Version", editable: true, type: "string", width: 120},
        {field: "maxVersion", headerName: "Max. Version", editable: true, type: "string", width: 120},
        {field: "description", headerName: "Beschreibung", flex: 1, editable: true, type: "string"},
        {field: "hostSystemCustomId", headerName: "Referenz", flex: 1, editable: true, type: "string"},
        {field: "isDocumentation", headerName: t('adminHostSystems.columns.isDocumentation'), editable: true, type: "boolean", width: 150},
        {
            field: "filterTags",
            headerName: t('adminHostSystems.columns.filterTags'),
            flex: 1,
            sortable: false,
            editable: true,
            renderCell: (params) => {
                const identifiers = (params.value as string[]) || [];
                if (identifiers.length === 0) {
                    return <Typography variant="caption">-</Typography>;
                }
                return (
                    <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                        {identifiers.map((identifier) => (
                            <Chip key={identifier} label={resolveFilterTagName(identifier)} size="small" />
                        ))}
                    </Stack>
                );
            },
            renderEditCell: renderFilterTagsEditCell,
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 100,
            cellClassName: 'actions',
            getActions: ({id}) => {
                const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

                if (isInEditMode) {
                    return [
                        <GridActionsCellItem
                            icon={<SaveIcon/>}
                            label="Save"
                            onClick={handleSaveClick(id)}
                        />,
                        <GridActionsCellItem
                            icon={<CancelIcon/>}
                            label="Cancel"
                            className="textPrimary"
                            onClick={handleCancelClick(id)}
                            color="inherit"
                        />,
                    ];
                }

                return [
                    <GridActionsCellItem
                        icon={<EditIcon/>}
                        label="Edit"
                        className="textPrimary"
                        onClick={handleEditClick(id)}
                        color="inherit"
                    />,
                    <GridActionsCellItem
                        icon={<DeleteIcon/>}
                        label="Delete"
                        onClick={handleDeleteClick(id)}
                        color="inherit"
                    />,
                ];
            },
        }
    ];

    return <>
        <Dialog
            open={selectedRow != undefined}
            onClose={handleDeleteDialogClose(false)}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogTitle id="alert-dialog-title">
                {"Löschen"}
            </DialogTitle>
            <DialogContent>
                <DialogContentText id="alert-dialog-description">
                    Möchten Sie den Eintrag wirklich löschen?
                </DialogContentText>
                {selectedRow != undefined && <>
                    <DialogContentText fontWeight={"bold"} id="alert-dialog-description">
                        Zum löschen {rows.find(r => r.id == selectedRow)?.name} in das Textfeld eingeben
                    </DialogContentText></>}
                <TextField  value={deleteEntryValue} onChange={target => setDeleteEntryValue(target.target.value)} fullWidth margin={"dense"}/>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleDeleteDialogClose(false)} color={"error"}>Nein</Button>
                <Button onClick={handleDeleteDialogClose(true)} color={"success"} autoFocus>
                    Ja
                </Button>
            </DialogActions>
        </Dialog>
        <Box sx={{display: 'grid', gap: 2, margin: 4, marginBottom: 0, gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)'}}}>
            <TextField
                label={t('adminHostSystems.filters.name')}
                value={filterName}
                onChange={(event) => setFilterName(event.target.value)}
                size="small"
            />
            <TextField
                label={t('adminHostSystems.filters.reference')}
                value={filterReference}
                onChange={(event) => setFilterReference(event.target.value)}
                size="small"
            />
            <TextField
                label={t('adminHostSystems.filters.minVersion')}
                value={filterMinVersion}
                onChange={(event) => setFilterMinVersion(event.target.value)}
                size="small"
            />
            <TextField
                label={t('adminHostSystems.filters.maxVersion')}
                value={filterMaxVersion}
                onChange={(event) => setFilterMaxVersion(event.target.value)}
                size="small"
            />
            <Select
                multiple
                displayEmpty
                value={filterTagsSelection}
                onChange={(event: SelectChangeEvent<string[]>) => {
                    const value = event.target.value as string[];
                    setFilterTagsSelection(value);
                }}
                renderValue={(selected) => {
                    if ((selected as string[]).length === 0) {
                        return t('adminHostSystems.filters.filterTags');
                    }
                    return (selected as string[])
                        .map(identifier => resolveFilterTagName(identifier))
                        .join(', ');
                }}
                size="small"
                sx={{ minWidth: 200 }}
            >
                {filterTags.map((tag) => (
                    <MenuItem key={tag.identifier} value={tag.identifier}>
                        <Checkbox checked={filterTagsSelection.indexOf(tag.identifier || '') > -1} />
                        <ListItemText primary={tag.name} />
                    </MenuItem>
                ))}
            </Select>
        </Box>
        <TableContainer component={Paper} style={{margin: 4}}>
            <DataGrid<HostSystemRow>
                editMode={"row"}
                getRowId={(row) => row.id!}
                slots={{
                    toolbar: () => <EditToolbar setRows={setRows} setRowModesModel={setRowModesModel} />,
                }}
                rowModesModel={rowModesModel}
                onRowModesModelChange={handleRowModesModelChange}
                onRowEditStart={handleRowEditStart}
                onRowEditStop={handleRowEditStop}
                processRowUpdate={processRowUpdate}
                onProcessRowUpdateError={(error) => console.error(error)}
                columns={columns}
                rows={filteredRows}
                loading={isFetching}
                autoHeight={true}/>
        </TableContainer>
    </>
}
